import { useCallback, useEffect, useState } from "react"
import { CustomPrompt } from "@/shared/types/prompt"
import { deleteCustomPrompt, saveCustomPrompt, subscribeToCustomPrompts } from "@/shared/services/prompts"
import { getCustomPromptDocId, MAX_CUSTOM_PROMPT_LENGTH, normalizePromptText } from "@/shared/utils/promptIds"

interface UseCustomPromptsResult {
  prompts: CustomPrompt[]
  loading: boolean
  error: string | null
  saving: boolean
  savePrompt: (promptText: string) => Promise<CustomPrompt>
  deletePrompt: (promptId: string) => Promise<void>
}

const upsertPrompt = (prompts: CustomPrompt[], prompt: CustomPrompt): CustomPrompt[] => {
  const withoutExisting = prompts.filter(existingPrompt => existingPrompt.id !== prompt.id)
  return [prompt, ...withoutExisting]
}

const useCustomPrompts = (userId: string | null | undefined): UseCustomPromptsResult => {
  const [prompts, setPrompts] = useState<CustomPrompt[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) {
      setPrompts([])
      setLoading(false)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    const unsubscribe = subscribeToCustomPrompts(
      userId,
      nextPrompts => {
        setPrompts(nextPrompts)
        setLoading(false)
      },
      error => {
        console.error("Failed to load saved prompts:", error)
        setError("Failed to load saved prompts. Please refresh and try again.")
        setLoading(false)
      }
    )

    return unsubscribe
  }, [userId])

  const savePrompt = useCallback(
    async (promptText: string): Promise<CustomPrompt> => {
      if (!userId) {
        throw new Error("You must be signed in to save prompts.")
      }

      const trimmedPrompt = promptText.trim()
      if (!trimmedPrompt) {
        throw new Error("Prompt text is required.")
      }
      if (trimmedPrompt.length > MAX_CUSTOM_PROMPT_LENGTH) {
        throw new Error(`Prompt must be ${MAX_CUSTOM_PROMPT_LENGTH} characters or less.`)
      }

      const existingPrompt = prompts.find(
        prompt => normalizePromptText(prompt.name) === normalizePromptText(trimmedPrompt)
      )
      if (existingPrompt) {
        return existingPrompt
      }

      setSaving(true)
      setError(null)
      try {
        const savedPrompt = await saveCustomPrompt(userId, trimmedPrompt)
        setPrompts(prev => upsertPrompt(prev, savedPrompt))
        return savedPrompt
      } catch (err) {
        console.error("Error saving custom prompt:", err)
        setError("Failed to save prompt")
        throw err
      } finally {
        setSaving(false)
      }
    },
    [prompts, userId]
  )

  const deletePrompt = useCallback(
    async (promptId: string): Promise<void> => {
      if (!userId) {
        throw new Error("You must be signed in to delete prompts.")
      }

      const promptDocId = getCustomPromptDocId(promptId)
      if (!promptDocId) {
        throw new Error("Invalid custom prompt id.")
      }

      setError(null)
      try {
        await deleteCustomPrompt(userId, promptDocId)
        setPrompts(prev => prev.filter(prompt => prompt.id !== promptId))
      } catch (err) {
        console.error("Error deleting custom prompt:", err)
        setError("Failed to delete prompt")
        throw err
      }
    },
    [userId]
  )

  return { prompts, loading, error, saving, savePrompt, deletePrompt }
}

export { useCustomPrompts }
export type { UseCustomPromptsResult }
